import React from 'react';
import {
  Box, HStack, Pressable, Text, VStack,
} from '@gluestack-ui/themed';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTenantGradingSettings } from '@/common/context/TenantSettingsStore';

interface DotLevelSelectorProps {
  name: string;
  dimension: string;
  readonly?: boolean;
}

/**
 * Dot-based level selector for Module Meta Editor.
 * Connected to react-hook-form.
 */
const DotLevelSelector: React.FC<DotLevelSelectorProps> = ({
  name,
  dimension,
  readonly = false,
}) => {
  const { setValue, control } = useFormContext();
  const { performanceLevels } = useTenantGradingSettings();
  const rawValue = useWatch({ control, name });

  // Default to the first level if no value is set
  const currentValueId = rawValue || performanceLevels[0]?.id;
  const currentLevelIndex = performanceLevels.findIndex((l) => l.id === currentValueId);

  const handleSelect = (levelId: string) => {
    if (readonly) return;
    setValue(name, levelId, { shouldDirty: true });
  };

  // Capitalize dimension key for the label (e.g. content -> Content)
  const label = dimension.charAt(0).toUpperCase() + dimension.slice(1);

  return (
    <HStack space="md" alignItems="center" justifyContent="space-between" py="$1">
      <Text
        size="xs"
        color="$textSubtle"
        letterSpacing="$sm"
        flex={1}
      >
        {label}
      </Text>
      <HStack space="xs">
        {performanceLevels.map((level, index) => {
          const isActive = index <= currentLevelIndex;

          return (
            <VStack key={level.id} space="xs" alignItems="center">
              <Pressable
                onPress={() => handleSelect(level.id)}
                disabled={readonly}
                p="$0.5"
              >
                <Box
                  width={14}
                  height={14}
                  borderRadius="$full"
                  bg={isActive ? '$brandBase' : '$backgroundLight200'}
                  borderWidth={1}
                  borderColor={isActive ? '$brandBase' : '$borderLight'}
                  justifyContent="center"
                  alignItems="center"
                  sx={{
                    _web: {
                      transition: 'all 0.1s ease-in-out',
                      cursor: readonly ? 'default' : 'pointer',
                      ':hover': {
                        borderColor: '$brandBase',
                        bg: isActive ? '$brandBase' : '$backgroundLight300',
                      },
                    },
                  }}
                />
              </Pressable>
            </VStack>
          );
        })}
      </HStack>
    </HStack>
  );
};

export default DotLevelSelector;
